/**
 * src/components/ThemeSwitcher.jsx — dashboard theme picker.
 *
 * Lives in the sidebar footer (see App.jsx), not Settings — Settings is
 * owner-only, and an artist's own theme is their own choice, not the
 * studio's.
 *
 * Each swatch sets `data-theme` on itself, so the [data-theme="..."] rules
 * in styles.css resolve --bg/--surface/--accent locally on that one button
 * and the conic-gradient below previews the theme's real tokens — nothing
 * duplicated in JS to fall out of sync with the CSS.
 */
import { THEMES, THEME_LABELS, setTheme } from '../lib/theme.js';

/**
 * @param {object} props
 * @param {string} props.value Currently active theme id.
 * @param {(theme: string) => void} props.onChange Called after the theme has
 *   already been applied and saved to localStorage — for callers that also
 *   want to persist it elsewhere (e.g. the artist's account).
 * @returns {JSX.Element}
 */
export default function ThemeSwitcher({ value, onChange }) {
  return (
    <div className="theme-swatch-row" role="group" aria-label="Theme">
      {THEMES.map((theme) => {
        const selected = value === theme;
        const label = THEME_LABELS[theme] ?? theme;
        return (
          <button
            key={theme}
            type="button"
            data-theme={theme}
            className={`theme-swatch${selected ? ' selected' : ''}`}
            onClick={() => {
              setTheme(theme);
              onChange?.(theme);
            }}
            title={label}
            aria-label={label}
            aria-pressed={selected}
          />
        );
      })}
    </div>
  );
}
